// "Scan ticket" section — TICKETING-SPEC.md §5.4.
//
// Staff scan a guest's ticket QR at the door, or type the short code when a
// camera will not focus. Both go to POST /tickets/redeem, which flips the ticket
// from 'issued' to 'used' with a conditional update — so a second scan of the
// same ticket cannot succeed, and comes back saying when the first one was.
//
// Built on RedeemVoucher.js, which solves the identical problem for vouchers:
// same session, same shared QrScanView, same error handling.
//
// Only staff (validatetokenrestaurantuser) can burn a ticket; the staff identity
// is the Bearer token (req.decoded.userid), never the body.
import React, { useState } from 'react';
import axios from 'axios';
import { API, authHeaders, describeError } from './api';
import QrScanView from '../../components/QrScanView';

// What the backend's refusal reasons mean to a person standing at a door.
const REASON_TEXT = {
  unknown_code: 'No ticket with that code.',
  already_used: 'This ticket has already been scanned.',
  ticket_cancelled: 'This ticket was cancelled.',
  ticket_refunded: 'This ticket was refunded.',
  code_required: 'Scan or type a ticket code.',
  event_cancelled: 'This event has been cancelled — do not admit.',
};

// "Tue 27 Oct, 19:00" — the date matters at a door that runs a different
// event every night.
const eventWhen = (event) => {
  if (!event) return '';
  const d = event.date ? new Date(`${event.date}T00:00:00`) : null;
  const day = d && !isNaN(d.getTime())
    ? d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })
    : event.date || '';
  return [day, event.start].filter(Boolean).join(', ');
};

/*
 * A scanned QR may be either the bare code or the ticket-page URL, depending on
 * whether the guest showed the code or the page. Accept both rather than making
 * door staff care which — the last path segment of our own URL is the code.
 */
const codeFrom = (scanned) => {
  const text = String(scanned || '').trim();
  if (!text) return '';
  if (text.includes('/t/')) {
    const tail = text.split('/t/').pop().split(/[?#]/)[0];
    return tail.trim().toUpperCase();
  }
  return text.toUpperCase();
};

export default function ScanTicket({ onAuthExpired }) {
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null); // { ok, ticket, event }
  const [refused, setRefused] = useState(null); // { reason, ticket, event }

  const submit = async (override, { force = false } = {}) => {
    if (busy) return;
    setError('');
    setResult(null);
    setRefused(null);

    const value = codeFrom(override !== undefined ? override : code);
    if (!value) {
      setError('Scan or type the ticket code.');
      return;
    }

    setBusy(true);
    try {
      const res = await axios.post(
        `${API}/tickets/redeem`,
        force ? { code: value, force: true } : { code: value },
        { headers: authHeaders() },
      );
      setResult(res.data || {});
      setCode('');
    } catch (err) {
      // Only a 401 is an expired session. A 403 here used to mean "ticket for
      // another venue" and logged the door person out mid-queue.
      if (err && err.response && err.response.status === 401) {
        if (onAuthExpired) {
          onAuthExpired();
          return;
        }
      }
      /*
       * A 409 is not a failure of the request — it is the door telling staff
       * something true and useful ("already scanned at 19:42"). Render it as an
       * answer, not as an error.
       */
      const data = err && err.response && err.response.data;
      if (err && err.response && err.response.status === 409 && data) {
        setRefused({ ...data, code: value });
        setCode('');
        return;
      }
      setError(describeError(err));
    } finally {
      setBusy(false);
    }
  };

  const usedAt = refused && refused.ticket && refused.ticket.usedAt
    ? new Date(refused.ticket.usedAt).toLocaleString()
    : null;

  return (
    <div className="staff-card">
      <h2>Scan ticket</h2>
      <p className="staff-hint">
        Scan the guest’s ticket QR — or type the short code they show you — to let
        them in.
      </p>

      {error ? <div className="staff-alert is-error">{error}</div> : null}

      {result && result.ok ? (
        <div className="staff-alert is-ok">
          <div className="staff-reward">
            <div className="staff-reward-icon">✅</div>
            <div className="staff-reward-name">
              {(result.ticket && result.ticket.type) || 'Ticket'} — admitted
            </div>
            {result.ticket && result.ticket.buyer ? (
              <div className="staff-reward-ref">{result.ticket.buyer}</div>
            ) : null}
            {result.event ? (
              <div className="staff-reward-ref">
                {result.event.name}{eventWhen(result.event) ? ` · ${eventWhen(result.event)}` : ''}
              </div>
            ) : null}
            {/* The venue's own question, e.g. allergies — the one thing door
                staff may actually need to act on. */}
            {result.ticket && result.ticket.answer ? (
              <div className="staff-reward-ref">“{result.ticket.answer}”</div>
            ) : null}
          </div>
        </div>
      ) : null}

      {refused ? (
        <div className="staff-alert is-error">
          <div className="staff-reward">
            <div className="staff-reward-icon">⛔️</div>
            <div className="staff-reward-name">
              {refused.reason === 'other_venue'
                ? `This ticket is for ${refused.venue || 'another venue'}.`
                : refused.reason === 'wrong_date'
                  ? `This ticket is for another date: ${eventWhen(refused.event)}.`
                  : REASON_TEXT[refused.reason] || String(refused.reason || '').replace(/_/g, ' ')}
            </div>
            {refused.event && refused.reason !== 'wrong_date' ? (
              <div className="staff-reward-ref">{refused.event.name} · {eventWhen(refused.event)}</div>
            ) : null}
            {refused.ticket ? (
              <div className="staff-reward-ref">
                {refused.ticket.type}{refused.ticket.buyer ? ` · ${refused.ticket.buyer}` : ''}
              </div>
            ) : null}
            {usedAt ? <div className="staff-reward-ref">Scanned {usedAt}</div> : null}
            {/* Doors open early and events run late: staff may decide. */}
            {refused.reason === 'wrong_date' && refused.code ? (
              <button
                className="staff-btn"
                type="button"
                style={{ marginTop: 10 }}
                onClick={() => submit(refused.code, { force: true })}
              >
                Admit anyway
              </button>
            ) : null}
          </div>
        </div>
      ) : null}

      {scanning ? (
        <>
          <QrScanView
            onScan={(text) => {
              setScanning(false);
              const value = codeFrom(text);
              setCode(value);
              // Straight through: at a door, one tap is the difference between a
              // queue that moves and one that does not.
              submit(value);
            }}
            hint="Point the camera at the guest’s ticket QR"
          />
          <button
            className="staff-btn staff-btn-ghost"
            type="button"
            style={{ marginTop: 10 }}
            onClick={() => setScanning(false)}
          >
            Cancel scan
          </button>
        </>
      ) : (
        <>
          <label className="staff-label" htmlFor="ticket-code">
            Ticket code
          </label>
          <input
            id="ticket-code"
            className="staff-input"
            placeholder="e.g. K7M2PQ4X"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
            autoCapitalize="characters"
            autoCorrect="off"
            spellCheck="false"
          />
          <button
            className="staff-btn"
            type="button"
            style={{ marginTop: 10 }}
            disabled={busy}
            onClick={() => submit()}
          >
            {busy ? 'Checking…' : 'Admit'}
          </button>
          <button
            className="staff-btn staff-btn-ghost"
            type="button"
            style={{ marginTop: 10 }}
            onClick={() => { setScanning(true); setError(''); setResult(null); setRefused(null); }}
          >
            Scan QR
          </button>
        </>
      )}
    </div>
  );
}
